import client from './client';
import type { ApiResponse, SMTP, SMTPHeader } from '../types';

interface CreateSMTPRequest {
  name: string;
  host: string;
  username?: string;
  password?: string;
  from_address: string;
  ignore_cert_errors?: boolean;
  interface?: string;
  max_emails_per_hour?: number;
  warmup_enabled?: boolean;
  warmup_start_date?: string;
  warmup_days?: number;
  warmup_target_daily?: number;
  dkim_selector?: string;
}

// SendingProfile extends the shared SMTP type with the warmup and
// deliverability fields returned by the API (the shared SMTP interface in
// types/index.ts predates them).
export interface SendingProfile extends SMTP {
  max_emails_per_hour?: number;
  warmup_enabled?: boolean;
  warmup_start_date?: string;
  warmup_days?: number;
  warmup_target_daily?: number;
  dkim_selector?: string;
}

export interface SMTPHealth {
  smtp_id: number;
  attempted: number;
  succeeded: number;
  failed: number;
  retrying: number;
  queued: number;
  backoff: number;
  success_rate: number;
  last_error?: string;
  last_error_time?: string;
  note?: string;
  warmup_enabled?: boolean;
  warmup_day?: number;
  warmup_days?: number;
  warmup_daily_cap?: number;
  warmup_target_daily?: number;
  warmup_sent_today?: number;
  dkim_selector?: string;
}

export interface DNSRecordCheck {
  found: boolean;
  record?: string;
  error?: string;
}

export interface MXCheck {
  found: boolean;
  records?: string[];
  error?: string;
}

export interface DKIMCheck {
  selector?: string;
  found: boolean;
  record?: string;
  has_version: boolean;
  has_key_type: boolean;
  has_public_key: boolean;
  error?: string;
}

export interface PTRCheck {
  host: string;
  addresses?: string[];
  ptr_names?: string[];
  found: boolean;
  forward_confirmed: boolean;
  error?: string;
}

export interface SMTPPreflightResult {
  smtp_ok: boolean;
  smtp_error?: string;
  domain?: string;
  spf: DNSRecordCheck;
  dmarc: DNSRecordCheck;
  mx: MXCheck;
  dkim?: DKIMCheck;
  ptr?: PTRCheck;
  warnings: string[];
  note: string;
}

// warmupStatusLine summarizes the profile's warmup state for display,
// e.g. "warmup day 6/21 — cap 160/day".
export function warmupStatusLine(health: SMTPHealth): string | null {
  if (!health.warmup_enabled) return null;
  const cap = health.warmup_daily_cap ?? 0;
  if (cap <= 0) return 'Warmup enabled — no daily cap configured';
  const sent = health.warmup_sent_today ?? 0;
  const day = health.warmup_day ?? 0;
  const days = health.warmup_days ?? 0;
  if (day <= 0) {
    return `Warmup not started — capped at ${cap}/day until the start date`;
  }
  if (days > 0 && day > days) {
    return `Warmup complete — daily cap ${cap}/day (sent ${sent} today)`;
  }
  return `Warmup day ${day}/${days} — cap ${cap}/day (sent ${sent} today)`;
}

// UpdateSMTPRequest is a PUT-style full replace: the handler requires the
// id in the body and rewrites custom headers wholesale, so callers should
// pass back the existing values for fields they are not changing.
export type UpdateSMTPRequest = Partial<CreateSMTPRequest> & {
  id?: number;
  headers?: SMTPHeader[];
};

export const smtpApi = {
  // Go returns a bare SendingProfile[] (not { data: [...] }).
  getAll: () =>
    client.get<SendingProfile[]>('/smtp/'),

  getById: (id: number) =>
    client.get<SendingProfile>(`/smtp/${id}`),

  create: (data: CreateSMTPRequest) =>
    client.post<SendingProfile>('/smtp/', data),

  // Go's PutSMTP requires the body's id to match the URL id.
  update: (id: number, data: UpdateSMTPRequest) =>
    client.put<SendingProfile>(`/smtp/${id}`, { ...data, id }),

  delete: (id: number) =>
    client.delete<ApiResponse<null>>(`/smtp/${id}`),

  sendTestEmail: (smtpId: number, email: string) =>
    client.post<ApiResponse<null>>('/util/send_test_email', {
      smtp_id: smtpId,
      email: email,
    }),

  getHealth: (id: number) =>
    client.get<SMTPHealth>(`/smtp/${id}/health`),

  runPreflight: (id: number) =>
    client.post<SMTPPreflightResult>(`/smtp/${id}/preflight`),

  sendCampaignTest: (body: { templateName: string; pageName?: string; smtpName: string; url: string; email: string }) =>
    client.post<ApiResponse<null>>('/util/send_test_email', {
      template: { name: body.templateName },
      page: { name: body.pageName ?? '' },
      smtp: { name: body.smtpName },
      url: body.url,
      email: body.email,
      first_name: 'Test',
      last_name: 'Operator',
    }),
};
